import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useHousehold } from "@/contexts/HouseholdContext";
import { hasProAccess } from "@/lib/plans";
import UpsellCard from "@/components/UpsellCard";
import { fmtCurrency, fmtCompact, CATEGORY_COLORS } from "@/lib/format";
import {
    BarChart, Bar, ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid, Legend,
    LineChart, Line, PieChart, Pie, Cell,
} from "recharts";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";

const PALETTE = ["#10B981","#6366F1","#0EA5E9","#F59E0B","#EF4444","#8B5CF6","#EC4899","#14B8A6"];

export default function Analytics() {
    const { user } = useAuth();
    const { isMember } = useHousehold();
    const cur = user?.currency || "USD";
    const [period, setPeriod] = useState("month");
    const [dash, setDash] = useState(null);
    const [catData, setCatData] = useState([]);
    const canView = hasProAccess(user, isMember);

    useEffect(() => {
        if (!canView) return;
        api.get(`/dashboard?period=${period}`).then(({ data }) => setDash(data));
        api.get(`/analytics/category?months=6`).then(({ data }) => setCatData(data));
    }, [period, canView]);

    const series = dash?.income_vs_expense_series || [];
    const catSeries = useMemo(
        () => catData.map((c, i) => ({ name: c.category, value: c.total, fill: CATEGORY_COLORS[c.category] || PALETTE[i % PALETTE.length] })),
        [catData]
    );

    if (!canView) {
        return (
            <div className="space-y-4" data-testid="analytics-page">
                <div>
                    <h1 className="font-display text-2xl font-extrabold tracking-tight text-slate-900">Analytics</h1>
                    <p className="text-sm text-slate-500 mt-1">Trends, comparisons, and where your money flows.</p>
                </div>
                <UpsellCard
                    feature="Analytics"
                    description="Upgrade to Pro to unlock spending trends, category breakdowns, and subscription mix charts."
                />
            </div>
        );
    }

    return (
        <div className="space-y-4" data-testid="analytics-page">
            <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
                <div>
                    <h1 className="font-display text-2xl font-extrabold tracking-tight text-slate-900">Analytics</h1>
                    <p className="text-sm text-slate-500 mt-1">Trends, comparisons, and where your money flows.</p>
                </div>
                <Tabs value={period} onValueChange={setPeriod}>
                    <TabsList className="bg-white border border-slate-200/80 rounded-xl p-1">
                        <TabsTrigger value="month" className="rounded-lg data-[state=active]:bg-indigo-50 data-[state=active]:text-indigo-700 px-4">Month</TabsTrigger>
                        <TabsTrigger value="year" className="rounded-lg data-[state=active]:bg-indigo-50 data-[state=active]:text-indigo-700 px-4">Year</TabsTrigger>
                        <TabsTrigger value="all" className="rounded-lg data-[state=active]:bg-indigo-50 data-[state=active]:text-indigo-700 px-4">All time</TabsTrigger>
                    </TabsList>
                </Tabs>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                <div className="card-elev rounded-2xl p-6 lg:col-span-2">
                    <h3 className="font-display text-lg font-bold text-slate-900 mb-1">Income vs expenses</h3>
                    <p className="text-sm text-slate-500 mb-4">Last 6 months</p>
                    <div className="h-72">
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={series} margin={{ left: -12, right: 8 }} barCategoryGap="22%">
                                <CartesianGrid stroke="#F1F5F9" strokeDasharray="3 3" vertical={false} />
                                <XAxis dataKey="month" stroke="#94A3B8" fontSize={12} tickLine={false} axisLine={false} />
                                <YAxis stroke="#94A3B8" fontSize={12} tickLine={false} axisLine={false} tickFormatter={(v) => fmtCompact(v, cur)} />
                                <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #E2E8F0" }} formatter={(v) => fmtCurrency(v, cur)} />
                                <Legend iconType="circle" wrapperStyle={{ fontSize: 12 }} />
                                <Bar dataKey="income" fill="#10B981" radius={[8, 8, 0, 0]} />
                                <Bar dataKey="expense" fill="#6366F1" radius={[8, 8, 0, 0]} />
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                </div>

                <div className="card-elev rounded-2xl p-6">
                    <h3 className="font-display text-lg font-bold text-slate-900 mb-1">Net flow</h3>
                    <p className="text-sm text-slate-500 mb-4">Income − expenses</p>
                    <div className="h-72">
                        <ResponsiveContainer width="100%" height="100%">
                            <LineChart data={series.map(s => ({ ...s, net: s.income - s.expense }))} margin={{ left: -12, right: 8 }}>
                                <CartesianGrid stroke="#F1F5F9" strokeDasharray="3 3" vertical={false} />
                                <XAxis dataKey="month" stroke="#94A3B8" fontSize={12} tickLine={false} axisLine={false} />
                                <YAxis stroke="#94A3B8" fontSize={12} tickLine={false} axisLine={false} tickFormatter={(v) => fmtCompact(v, cur)} />
                                <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #E2E8F0" }} formatter={(v) => fmtCurrency(v, cur)} />
                                <Line type="monotone" dataKey="net" stroke="#0EA5E9" strokeWidth={3} dot={{ r: 4, strokeWidth: 2, fill: "#fff" }} />
                            </LineChart>
                        </ResponsiveContainer>
                    </div>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                <div className="card-elev rounded-2xl p-6 lg:col-span-2">
                    <h3 className="font-display text-lg font-bold text-slate-900 mb-1">Top categories (6 months)</h3>
                    <p className="text-sm text-slate-500 mb-4">Where your money went</p>
                    <div className="h-72">
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart layout="vertical" data={catSeries.slice(0, 8)} margin={{ left: 32, right: 24 }}>
                                <CartesianGrid stroke="#F1F5F9" strokeDasharray="3 3" horizontal={false} />
                                <XAxis type="number" stroke="#94A3B8" fontSize={12} tickFormatter={(v) => fmtCompact(v, cur)} />
                                <YAxis type="category" dataKey="name" width={100} stroke="#475569" fontSize={12} tickLine={false} axisLine={false} />
                                <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #E2E8F0" }} formatter={(v) => fmtCurrency(v, cur)} />
                                <Bar dataKey="value" radius={[0, 8, 8, 0]}>
                                    {catSeries.slice(0, 8).map((c, i) => <Cell key={i} fill={c.fill} />)}
                                </Bar>
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                </div>

                <div className="card-elev rounded-2xl p-6">
                    <h3 className="font-display text-lg font-bold text-slate-900 mb-1">Subscription mix</h3>
                    <p className="text-sm text-slate-500 mb-4">Per month</p>
                    <SubscriptionMix cur={cur} />
                </div>
            </div>
        </div>
    );
}

function SubscriptionMix({ cur }) {
    const [data, setData] = useState([]);
    useEffect(() => {
        api.get("/subscriptions", { params: { status: "active" } }).then(({ data }) => {
            const byCat = {};
            data.forEach(s => {
                const monthly = (() => {
                    const c = parseFloat(s.cost);
                    if (s.billing_cycle === "weekly") return c * 4.345;
                    if (s.billing_cycle === "quarterly") return c / 3;
                    if (s.billing_cycle === "yearly") return c / 12;
                    return c;
                })();
                byCat[s.category] = (byCat[s.category] || 0) + monthly;
            });
            setData(Object.entries(byCat).map(([name, value], i) => ({ name, value, fill: PALETTE[i % PALETTE.length] })));
        });
    }, []);
    if (data.length === 0) return <div className="text-center text-sm text-slate-400 py-10">No active subs</div>;
    const total = data.reduce((s, d) => s + d.value, 0);
    return (
        <>
            <div className="h-44 relative">
                <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                        <Pie data={data} dataKey="value" nameKey="name" innerRadius={48} outerRadius={72} paddingAngle={2} stroke="none">
                            {data.map((c, i) => <Cell key={i} fill={c.fill} />)}
                        </Pie>
                        <Tooltip formatter={(v) => fmtCurrency(v, cur)} contentStyle={{ borderRadius: 12, border: "1px solid #E2E8F0" }} />
                    </PieChart>
                </ResponsiveContainer>
                <div className="absolute inset-0 grid place-items-center pointer-events-none">
                    <div className="text-center">
                        <div className="text-xs text-slate-500">Monthly</div>
                        <div className="font-display text-xl font-extrabold text-slate-900">{fmtCompact(total, cur)}</div>
                    </div>
                </div>
            </div>
            <div className="mt-4 space-y-2">
                {data.map(d => (
                    <div key={d.name} className="flex items-center justify-between text-sm">
                        <div className="flex items-center gap-2 min-w-0">
                            <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: d.fill }} />
                            <span className="text-slate-700 truncate">{d.name}</span>
                        </div>
                        <span className="font-semibold text-slate-900 tabular-nums">{fmtCurrency(d.value, cur)}</span>
                    </div>
                ))}
            </div>
        </>
    );
}
